import HelpCircle from 'assets/svg/icons/HelpCircle';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { useAiMetadata, useSetAiMetadata } from 'contexts/AIMetadataProvider';
import useWorldTheme from 'hooks/shared/useWorldTheme';
import type { FC, ReactNode } from 'react';
import { Children, cloneElement, isValidElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { IColors } from 'theme/colors';
import type { ConversationMetadata } from 'types/ai.types';
import { Modal } from '../../atoms/Modal';
import type { HelperProps } from './Helper.types';
import { twMerge } from 'tailwind-merge';

const colors = {
  primary: 'text-primary-800',
  expenses: 'text-expenses',
  revenues: 'text-revenues',
  bank: 'text-bank',
  taxes: 'text-taxes',
};

const backgroundColors = {
  primary: 'bg-primary-100',
  expenses: 'bg-expenses-100',
  revenues: 'bg-revenues-100',
  bank: 'bg-bank-100',
  taxes: 'bg-taxes-100',
};

const Title = ({
  children,
  color,
  className,
}: {
  children: any;
  color?: IColors;
  className?: string;
}) => {
  const _color = useWorldTheme();
  return (
    <div
      className={classNames(
        `font-bold text-xl font-degular py-2 px-3 rounded-lg mt-2`,
        colors[color || _color],
        backgroundColors[color || _color],
        className,
      )}
    >
      {children}
    </div>
  );
};

const SubTitle = ({
  children,
  color,
  className,
}: {
  children: ReactNode;
  color?: IColors;
  className?: string;
}) => {
  const _color = useWorldTheme();
  return (
    <div
      className={classNames(
        `font-bold text-lg font-degular`,
        colors[color || _color],
        className,
      )}
    >
      {children}
    </div>
  );
};

const Paragraph = ({
  children,
  className,
}: {
  children: any;
  className?: string;
}) => (
  <div
    className={classNames('py-2 text-sm font-inter text-primary', className)}
  >
    {children}
  </div>
);

const Contact = ({ onClickHelp }: { onClickHelp?: () => void }) => {
  const { setIsHelperOpen } = useAiMetadata();
  const { t } = useTranslation();

  return (
    <div className="rounded-xl p-[1px] mt-3 ai-border-animated">
      <Button
        onClick={() => {
          setIsHelperOpen(true);
          onClickHelp?.();
        }}
        asDefault
        className="w-full p-2 text-taxes font-semibold bg-white rounded-xl hover:bg-taxes-50 transition-colors duration-300"
      >
        {/* TODO: add a dedicated translation key */}
        {t('yearly_review.ai_chat.title')}
      </Button>
    </div>
  );
};

const extractTextFromChildren = (children: ReactNode): string => {
  let textContent = '';

  const traverse = (child: ReactNode) => {
    if (typeof child === 'string' || typeof child === 'number') {
      textContent += child + '\n ';
    } else if (isValidElement(child) && child.props.children) {
      Children.forEach(child.props.children, traverse);
    }
  };

  Children.forEach(children, traverse);

  return textContent;
};

const Inline: FC<
  Pick<HelperProps, 'children' | 'color' | 'hideAI'> & {
    onAfterClickAI?: () => void;
  }
> = ({ color, children, hideAI, onAfterClickAI }) => {
  const worldColor = useWorldTheme();
  color = color || worldColor;

  const metadata = useMemo(() => {
    if (hideAI) return;
    return {
      documentType: 'helper',
      content: extractTextFromChildren(children),
    } as ConversationMetadata;
  }, [children, hideAI]);

  useSetAiMetadata(metadata, 'helper');

  return (
    <div className="">
      {Children.map(children, (child) =>
        isValidElement(child)
          ? cloneElement(child, { color: color } as any)
          : child,
      )}
      <div className="mt-3">
        <Contact
          onClickHelp={() => {
            onAfterClickAI?.();
          }}
        />
      </div>
    </div>
  );
};

const HelperComponent: FC<HelperProps> = ({
  children,
  color,
  trigger,
  name,
  iconClassName = '',
  className,
  hideAI,
  tracingEvent,
  triggerColor,
}) => {
  const worldColor = useWorldTheme();
  color = color || worldColor;

  const helperTriggerThemeMap = {
    primary: 'text-primary-950 hover:text-primary-700 focus:text-primary-700',
    expenses:
      'text-expenses-950 hover:text-expenses-700 focus:text-expenses-700',
    revenues:
      'text-revenues-950 hover:text-revenues-700 focus:text-revenues-700',
    bank: 'text-bank-950 hover:text-bank-700 focus:text-bank-700',
    taxes: 'text-taxes-950 hover:text-taxes-700 focus:text-taxes-700',
    gray: 'text-primary-400 hover:text-primary-500 focus:text-primary-500',
  };

  return (
    <Modal
      name={`tutorial helper: ${name || ''}`}
      dialogPanalClassName="pt-4"
      trigger={(api) =>
        trigger ? (
          trigger(() => api.openModal())
        ) : (
          <Button
            asDefault
            className={classNames('flex', className)}
            tracingEvent={tracingEvent}
            onClick={(e) => {
              e.stopPropagation();
              api.openModal();
            }}
          >
            <HelpCircle
              className={twMerge(
                'h-5 w-5 inline-block cursor-pointer',
                helperTriggerThemeMap[triggerColor || 'primary'] ||
                  helperTriggerThemeMap.primary,
                iconClassName,
              )}
            />
          </Button>
        )
      }
      color={color}
      render={() => (
        <Inline color={color} hideAI={hideAI}>
          {children}
        </Inline>
      )}
    />
  );
};

export default Object.assign(HelperComponent, {
  Title,
  SubTitle,
  Paragraph,
  Contact,
  Inline,
});
